/* The left column: a tree that only selects.
 *
 * Schools, and inside each the subjects that actually run a lesson there,
 * and inside those the lessons — the same hierarchy the reader's catalogue
 * walks. Everything else about a level — its data, its children, founding a
 * new one — happens in the middle; a tree that also edited would be a second
 * editor to keep in step.
 */

import { useState } from "react";
import type { ContentRow } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import type { Localized } from "../../lib/types";
import { DRAFT, type Ed, type Sel } from "./Content";

const name = (v: Localized | undefined, fallback: string): string =>
  typeof v === "string" ? v || fallback : v?.cs || fallback;

/** The short title of a lesson row: what follows the dash, if anything does. */
export const shortTitle = (row: ContentRow): string => {
  const t = row.meta?.title ?? row.path;
  const cut = t.split("—")[1];
  return (cut ?? t).trim();
};

const sameSel = (a: Sel, b: Sel) =>
  a.school === b.school && a.subject === b.subject && !a.sub && !a.creating
  && a.doc === b.doc && !!a.pages === !!b.pages && !!a.apps === !!b.apps
  && !!a.taxJson === !!b.taxJson;

export function EdTree({ ed }: { ed: Ed }) {
  const { ui } = useI18n();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) => setOpen((was) => {
    const next = new Set(was);
    if (!next.delete(key)) next.add(key);
    return next;
  });

  const docs = ed.rows.filter((r) => r.kind === "doc");
  const lessons = docs.filter((r) => r.meta?.type === "lesson");
  const pages = docs.filter((r) => r.meta?.type !== "lesson");
  const apps = ed.rows.filter((r) => r.kind === "app");

  const node = (key: string | null, label: string, target: Sel, opts: {
    count?: number; bold?: boolean; draft?: boolean; dot?: boolean; kids?: React.ReactNode;
  } = {}) => {
    const opened = key !== null && open.has(key);
    return (
      <div key={key ?? label}>
        <div className="edx-twig">
          {key !== null && opts.kids !== undefined
            ? <button type="button" className="edx-caret plain" aria-expanded={opened}
                      onClick={() => toggle(key)}>{opened ? "▾" : "▸"}</button>
            : <span className="edx-caret" />}
          <button type="button"
                  className={"edx-node plain" + (sameSel(ed.sel, target) ? " on" : "")}
                  onClick={() => { ed.go(target); if (key !== null && !opened) toggle(key); }}>
            {opts.dot ? <span className={"edx-dot" + (opts.draft ? " draft" : "")} /> : null}
            <span className={"edx-nm" + (opts.bold ? " b" : "")}>{label}</span>
            {opts.count !== undefined ? <span className="edx-n">{opts.count}</span> : null}
          </button>
        </div>
        {opened ? <div className="edx-kids">{opts.kids}</div> : null}
      </div>
    );
  };

  const lessonNode = (row: ContentRow, target: Sel) =>
    node(null, shortTitle(row), { ...target, doc: row.path }, {
      dot: true, draft: row.status === DRAFT, count: row.meta?.year,
    });

  const strandIds = ed.tax?.strandIds ?? [];

  return (
    <nav className="edx-tree">
      {node(null, ui("content.schools"), {}, { bold: true, count: strandIds.length })}
      {strandIds.map((sid) => {
        const strand = ed.tax?.strands[sid];
        const mine = lessons.filter((r) => r.meta?.strand?.includes(sid));
        const subjects = [...new Set(mine.map((r) => r.meta?.subject ?? ""))].sort();
        return node(sid, name(strand?.name, sid), { school: sid }, {
          count: mine.length,
          kids: subjects.map((sub) => {
            const inSubject = mine
              .filter((r) => r.meta?.subject === sub)
              .sort((a, b) => (a.meta?.year ?? 0) - (b.meta?.year ?? 0));
            return node(`${sid}/${sub}`, name(ed.tax?.subjects[sub]?.name, sub),
              { school: sid, subject: sub }, {
                count: inSubject.length,
                kids: inSubject.map((r) => lessonNode(r, { school: sid, subject: sub })),
              });
          }),
        });
      })}

      <div className="edx-troot" />
      {node("pages", ui("content.pages"), { pages: true }, {
        bold: true, count: pages.length,
        kids: pages.map((r) => node(null, r.meta?.title || r.path, { pages: true, doc: r.path },
          { dot: true, draft: r.status === DRAFT })),
      })}
      {node(null, ui("content.apps"), { apps: true }, { count: apps.length })}
      {node(null, ui("content.taxonomyJson"), { taxJson: true, doc: "taxonomy.json" })}
    </nav>
  );
}
